import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import FooterBrand from '@components/common/FooterBrand';
import ComparisonPagination from '@pages/Comparison/components/ComparisonPagination';
import mockInstructions from '@data/mockInstructions';
import {
  buildInstructionsChartBundlePayload,
  getInstructionsOneYearRange,
} from '@apis/instructionsChartMappers';
import { mapDisclosureNewsToInfoItems } from '@apis/instructionsMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useDisclosureNews } from '@hooks/useDisclosureNews';
import { useDisclosureReportLink } from '@hooks/useDisclosureReportLink';
import { useDividendStockPriceHistory } from '@hooks/useDividendStockPriceHistory';
import { getTotalPages, slicePage } from '@utils/pagination';
import { notify } from '@utils/notify';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';
import type {
  InstructionsImpactData,
  InstructionsInfoItem,
  InstructionsInfoTabKey,
} from '@app-types/instructions';
import InstructionsImpactSection, {
  type InstructionsImpactState,
} from './components/InstructionsImpactSection';
import InstructionsInformationSection from './components/InstructionsInformationSection';
import { DEFAULT_INFO_TAB_KEY } from './components/instructionsInfoFilters';
import './InstructionsPage.less';

/** Disclosure rows per page, matching the 7 rows the Figma layout shows. */
export const INSTRUCTIONS_UI_PAGE_SIZE = 7;
const INITIAL_PAGE = 1;
const NEWS_FETCH_ERROR_MESSAGE = '適時開示情報の取得に失敗しました。';
const REPORT_FETCH_ERROR_MESSAGE = '適時開示レポートの取得に失敗しました。';
const PRICE_FETCH_ERROR_MESSAGE = '株価データの取得に失敗しました。';

/**
 * Instructions / Timely Disclosure screen (route /instructions).
 * Web: 3175:7680 (626 × 1092.534). Mobile: 3177:9206 (375 × 1315.534).
 * Render order: nav → impact → information → pagination → footer.
 * The information list and Chart5 iframe are API-backed with one shared JST
 * range. Disclosure filtering remains a backend responsibility.
 */
const InstructionsPage: React.FC = () => {
  const data = mockInstructions;
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const oneYearRange = useMemo(() => getInstructionsOneYearRange(), []);
  const initialActiveTabKey =
    data.information.tabs.find((tab) => tab.active)?.key ?? DEFAULT_INFO_TAB_KEY;
  const [activeTabKey, setActiveTabKey] =
    useState<InstructionsInfoTabKey>(initialActiveTabKey);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const newsQuery = useDisclosureNews(
    activeTabKey,
    oneYearRange.chart5From,
    oneYearRange.chart5To,
  );
  const priceQuery = useDividendStockPriceHistory(
    oneYearRange.chart5From,
    oneYearRange.chart5To,
    'd',
  );
  const reportLinkMutation = useDisclosureReportLink();
  const reportRequestPendingRef = useRef(false);
  const refetchNews = newsQuery.refetch;

  // Every tab click refreshes its API data; paging always restarts at page 1.
  const handleTabClick = useCallback((tabKey: InstructionsInfoTabKey) => {
    setCurrentPage(INITIAL_PAGE);
    if (tabKey === activeTabKey) {
      void refetchNews();
      return;
    }
    setActiveTabKey(tabKey);
  }, [activeTabKey, refetchNews]);

  const availableNewsRows = useMemo(
    () => newsQuery.isError ? [] : newsQuery.data?.rows ?? [],
    [newsQuery.data?.rows, newsQuery.isError],
  );

  const availableItems = useMemo(
    () => mapDisclosureNewsToInfoItems(availableNewsRows),
    [availableNewsRows],
  );

  const chartPayload = useMemo(
    () => buildInstructionsChartBundlePayload({
      ticker: ticker ?? '',
      mkt: mkt ?? '',
      priceCsv: priceQuery.data,
      // A pending/error category actively clears the previous category's
      // markers while keeping an already loaded price chart mounted.
      disclosureRows: newsQuery.isPending || newsQuery.isError
        ? []
        : newsQuery.data?.rows,
    }),
    [
      mkt,
      newsQuery.data?.rows,
      newsQuery.isError,
      newsQuery.isPending,
      priceQuery.data,
      ticker,
    ],
  );
  const availableItemCount = availableItems.length;
  const totalPages = getTotalPages(availableItemCount, INSTRUCTIONS_UI_PAGE_SIZE);

  const impactData = useMemo<InstructionsImpactData>(() => ({
    ...data.impact,
    legend: data.impact.legend.map((item) => (
      item.key === 'company' ? { ...item, label: ticker ?? '--' } : item
    )),
  }), [data.impact, ticker]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!newsQuery.error || !shouldNotifyDataRequestError(newsQuery.error)) return;

    notify({
      level: 'error',
      message: getApiParamErrorNotificationMessage(newsQuery.error)
        ?? NEWS_FETCH_ERROR_MESSAGE,
    });
  }, [newsQuery.error]);

  useEffect(() => {
    if (!priceQuery.error || !shouldNotifyDataRequestError(priceQuery.error)) return;

    notify({
      level: 'error',
      message: getApiParamErrorNotificationMessage(priceQuery.error)
        ?? PRICE_FETCH_ERROR_MESSAGE,
    });
  }, [priceQuery.error]);

  const handleOpenReport = useCallback(async (
    item: InstructionsInfoItem,
  ): Promise<string> => {
    if (!item.pnac || !item.createdAt || reportRequestPendingRef.current) {
      throw new Error('Timely disclosure report link is unavailable.');
    }

    reportRequestPendingRef.current = true;
    try {
      return await reportLinkMutation.mutateAsync({
        pnac: item.pnac,
        createdAt: item.createdAt,
      });
    } catch (caught) {
      if (shouldNotifyDataRequestError(caught)) {
        notify({
          level: 'error',
          message: getApiParamErrorNotificationMessage(caught)
            ?? REPORT_FETCH_ERROR_MESSAGE,
        });
      }
      throw caught;
    } finally {
      reportRequestPendingRef.current = false;
    }
  }, [reportLinkMutation]);

  const listState = newsQuery.isPending
    ? 'loading'
    : newsQuery.isError
      ? 'error'
      : availableItems.length === 0
        ? 'empty'
        : 'ready';

  const impactState: InstructionsImpactState = priceQuery.isPending
    ? 'loading'
    : priceQuery.isError
      ? 'error'
      : !chartPayload
        ? 'empty'
        : 'ready';

  const disclosureState = newsQuery.isPending
    ? 'loading' as const
    : newsQuery.isError
      ? 'error' as const
      : 'ready' as const;

  const visibleInformation = useMemo(
    () => ({
      ...data.information,
      tabs: data.information.tabs.map((tab) => ({
        ...tab,
        active: tab.key === activeTabKey,
      })),
      items: slicePage(availableItems, currentPage, INSTRUCTIONS_UI_PAGE_SIZE),
    }),
    [data.information, availableItems, currentPage, activeTabKey],
  );

  return (
    <WidgetPageShell className="instructions">
      <WidgetNav />
      <main className="instructions__body">
        <InstructionsImpactSection
          data={impactData}
          payload={chartPayload}
          state={impactState}
          disclosureState={disclosureState}
        />
        <InstructionsInformationSection
          data={visibleInformation}
          activeTabKey={activeTabKey}
          onTabClick={handleTabClick}
          listState={listState}
          isOpeningReport={reportLinkMutation.isPending}
          onOpenReport={handleOpenReport}
        />
        {availableItemCount > 0 && (
          <ComparisonPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            nodeId="3450:32068"
          />
        )}
        <FooterBrand className="instructions__footer" />
      </main>
    </WidgetPageShell>
  );
};

export default InstructionsPage;
